"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.evaluateSync = exports.evaluateFast = exports.evaluate = void 0;
exports.removeJsonataMetadata = removeJsonataMetadata;
/**
 * This file is being compiled and bundled by esbuild into a jsonataWorker.js separately.
 * So it can be spawned inside worker_thread by the index.ts file.
 * Thanks to that, we can preemptively kill the worker_thread if data mapping is taking too long.
 */
const is_1 = __importDefault(require("@sindresorhus/is"));
const lodash_mergewith_1 = __importDefault(require("lodash.mergewith"));
const util_1 = require("util");
const dateTime_1 = require("./bindings/dateTime");
const omitArrayItem_1 = require("./bindings/omitArrayItem");
const omitField_1 = require("./bindings/omitField");
const stediJsonata_1 = require("./stediJsonata");
/**
 * Evaluates single JSONata expression against provided data.
 * @returns Evaluated value which can be null or undefined
 */
const evaluate = (_a) => __awaiter(void 0, [_a], void 0, function* ({ bindings = {}, input, mapping, targetExample, type, }) {
    const evaluatePromise = (0, util_1.promisify)((0, stediJsonata_1.stediJsonata)(mapping).evaluate);
    const bindingsWithConsts = Object.assign(Object.assign({}, bindings), { dateTime: dateTime_1.dateTime,
        omitArrayItem: omitArrayItem_1.omitArrayItem,
        omitField: omitField_1.omitField });
    const evaluationStartMs = performance.now();
    const evaluationResult = yield evaluatePromise(input, bindingsWithConsts);
    const evaluationEndMs = performance.now();
    const evaluationTimeMs = evaluationEndMs - evaluationStartMs;
    return processEvaluationResult({ evaluationResult, evaluationTimeMs, input, type, targetExample });
});
exports.evaluate = evaluate;
/**
 * Evaluates single JSONata expression against provided data using @stedi/jsonata
 * @returns Evaluated value which can be null or undefined
 */
const evaluateFast = (_a) => __awaiter(void 0, [_a], void 0, function* ({ bindings = {}, input, mapping, targetExample, type, }) {
    const bindingsWithConsts = Object.assign(Object.assign({}, bindings), { dateTime: dateTime_1.dateTime,
        omitArrayItem: omitArrayItem_1.omitArrayItem,
        omitField: omitField_1.omitField });
    const evaluationStartMs = performance.now();
    const expression = (0, stediJsonata_1.stediJsonata)(mapping, true);
    const evaluationResult = yield expression.evaluate(input, bindingsWithConsts);
    const evaluationEndMs = performance.now();
    const evaluationTimeMs = evaluationEndMs - evaluationStartMs;
    return processEvaluationResult({ evaluationResult, evaluationTimeMs, input, type, targetExample });
});
exports.evaluateFast = evaluateFast;
/**
 * Evaluates single JSONata expression against provided data synchronously.
 * @returns Evaluated value which can be null or undefined
 */
const evaluateSync = ({ bindings = {}, input, mapping, targetExample, type, }) => {
    const bindingsWithConsts = Object.assign(Object.assign({}, bindings), { dateTime: dateTime_1.dateTime,
        omitArrayItem: omitArrayItem_1.omitArrayItem,
        omitField: omitField_1.omitField });
    const evaluationStartMs = performance.now();
    const evaluationResult = (0, stediJsonata_1.stediJsonata)(mapping).evaluate(input, bindingsWithConsts);
    const evaluationEndMs = performance.now();
    const evaluationTimeMs = evaluationEndMs - evaluationStartMs;
    return processEvaluationResult({ evaluationResult, evaluationTimeMs, input, type, targetExample });
};
exports.evaluateSync = evaluateSync;
const processEvaluationResult = ({ evaluationResult, evaluationTimeMs, input, targetExample, type, }) => {
    const resultWithoutJsonataMetadata = removeJsonataMetadata(evaluationResult);
    if (type === "only_mapped_keys") {
        const omitResult = omitFieldsIfNecessary(resultWithoutJsonataMetadata);
        return {
            result: omitResult,
            evaluationTimeMs,
        };
    }
    /**
     * `merge` mutates the first argument.
     * To ensure the mutation does not happen, an empty object is provided.
     */
    if (type === "pass_through") {
        const mergeResult = mergeDocumentWithEvaluationResult(input, resultWithoutJsonataMetadata);
        const omitResult = omitFieldsIfNecessary(mergeResult);
        return {
            result: omitResult,
            evaluationTimeMs,
        };
    }
    if (type === "merge_with_target_example" && targetExample) {
        const mergeResult = mergeDocumentWithEvaluationResult(targetExample, resultWithoutJsonataMetadata);
        const omitResult = omitFieldsIfNecessary(mergeResult);
        return {
            result: omitResult,
            evaluationTimeMs,
        };
    }
};
/**
 * Jsonata attaches metadata to the result of evaluation.
 * @example {stops: [ {...}, {...}, sequence: true, keepSingleton: true ]}
 * The `sequence` and the `keepSingleton` are not part of the result and will be removed when `JSON.stringify` is called.
 */
function removeJsonataMetadata(val) {
    if (val === undefined) {
        return undefined;
    }
    return JSON.parse(JSON.stringify(val));
}
const mergeDocumentWithEvaluationResult = (document, evaluationResult) => {
    const evaluationResultIsPlainObject = is_1.default.plainObject(evaluationResult);
    const evaluationResultIsArrayOfPlainObjects = is_1.default.array(evaluationResult, is_1.default.plainObject);
    const shouldMerge = evaluationResultIsPlainObject || evaluationResultIsArrayOfPlainObjects;
    if (!shouldMerge) {
        return evaluationResult;
    }
    /**
     * `mergeWith` mutates the first argument.
     * To ensure the mutation does not happen, an empty object or array is provided.
     */
    const mutationProtection = is_1.default.array(document) ? [] : {};
    return (0, lodash_mergewith_1.default)(mutationProtection, document, evaluationResult, (mergeSourceValue, mergeTargetValue) => {
        if (shouldMergeValuesForField(mergeSourceValue, mergeTargetValue)) {
            // lodash docs: "[...] If customizer returns undefined, merging is handled by the method instead."
            // Explicitly returning undefined here to avoid confusion
            return undefined;
        }
        return mergeTargetValue;
    });
};
function shouldMergeValuesForField(mergeSourceValue, mergeTargetValue) {
    // Making sure we won't merge an array of primitives when using merge_with_target_example type
    const isSourceArrayOfPrimitives = is_1.default.array(mergeSourceValue, is_1.default.primitive);
    const isTargetValueArray = is_1.default.array(mergeTargetValue);
    const shouldNotMergeValues = isSourceArrayOfPrimitives && isTargetValueArray;
    if (shouldNotMergeValues) {
        return false;
    }
    return true;
}
/**
 * Skips any fields of an object that
 * 1. Are primitive strings with the value `$omitField` evaluates to.
 *  For example: `{ foo: "$omitField" }` -> `{}
 *  For example: `{ foo: "bar", baz: {"bazKey": "$omitField"} }` -> `{ foo: "bar", baz: {} }`
 *
 * 2. Are array of primitives that contain at least one value the `$omitField` evaluates to.
 *  For example: `{ foo: [ "$omitField", "bar" ] }` -> `{}`
 *
 * The skipping is bounded to the "closest" object.
 *  For example: `{"foo": [{"bar": "$omitField"}]}` -> `{"foo": [{}]}`
 *
 * Handles nested arrays and mixed arrays (primitives and objects).
 */
function omitFieldsIfNecessary(input) {
    if (is_1.default.array(input)) {
        const { array, shouldOmitField } = traverseArray(input);
        if (shouldOmitField) {
            return undefined;
        }
        return array;
    }
    if (input === omitField_1.omitField) {
        return undefined;
    }
    if (!is_1.default.plainObject(input)) {
        return input;
    }
    const inputWithoutOmittedFields = {};
    Object.keys(input).forEach((key) => {
        const value = input[key];
        if (is_1.default.primitive(value) && value !== omitField_1.omitField) {
            inputWithoutOmittedFields[key] = value;
        }
        if (is_1.default.plainObject(value)) {
            inputWithoutOmittedFields[key] = omitFieldsIfNecessary(value);
        }
        if (is_1.default.array(value)) {
            const { array, shouldOmitField } = traverseArray(value);
            if (!shouldOmitField) {
                inputWithoutOmittedFields[key] = array;
            }
        }
    });
    return inputWithoutOmittedFields;
    function traverseArray(array) {
        /**
         * The array has to be a value of some kind of object field.
         */
        let shouldOmitWholeField = false;
        const arrayWithoutOmittedFields = [];
        /**
         * For loop is used as we cannot break out of `reduce` or `forEach`.
         */
        for (const arrayValue of array) {
            /**
             * If the array contains a primitive that evaluates to `$omitField`, break the loop.
             * The whole field should be skipped.
             */
            if (arrayValue === omitField_1.omitField) {
                shouldOmitWholeField = true;
                break;
            }
            /**
             * If the array contains a primitive that evaluates to `$omitArrayItem`, skip this item and continue the loop.
             * Only this item should be skipped.
             */
            if (arrayValue === omitArrayItem_1.omitArrayItem) {
                continue;
            }
            if (is_1.default.primitive(arrayValue)) {
                arrayWithoutOmittedFields.push(arrayValue);
                continue;
            }
            if (is_1.default.plainObject(arrayValue)) {
                arrayWithoutOmittedFields.push(omitFieldsIfNecessary(arrayValue));
                continue;
            }
            if (is_1.default.array(arrayValue)) {
                /**
                 * Handle nested arrays.
                 */
                const { array: arrayWithOmittedFieldsForArrayValue, shouldOmitField } = traverseArray(arrayValue);
                if (shouldOmitField) {
                    shouldOmitWholeField = true;
                    break;
                }
                arrayWithoutOmittedFields.push(arrayWithOmittedFieldsForArrayValue);
                continue;
            }
        }
        return { shouldOmitField: shouldOmitWholeField, array: arrayWithoutOmittedFields };
    }
}
